"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
	type ReactNode,
} from "react";
import { useTranslations } from "next-intl";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";

const STORAGE_KEY = "eurolab-ruo-confirmed-v1";
const AGE_GATE_COOKIE = "age_gate_v1";
const AUTO_SHOW_DELAY = 600;

type ConfirmContextValue = {
	isConfirmed: boolean;
	requestConfirm: (onConfirmed?: () => void) => void;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

function setConsentPersistence() {
	try {
		localStorage.setItem(STORAGE_KEY, String(Date.now()));
	} catch {
		// ignore
	}
	document.cookie = `${AGE_GATE_COOKIE}=1; max-age=31536000; path=/; SameSite=Lax`;
}

function hasStoredConsent(): boolean {
	try {
		if (localStorage.getItem(STORAGE_KEY)) return true;
	} catch {
		// ignore
	}
	return document.cookie.split(";").some((c) => c.trim().startsWith(`${AGE_GATE_COOKIE}=`));
}

function subscribeConsent(_onStoreChange: () => void) {
	return () => {};
}

function getClientStoredConsent() {
	return hasStoredConsent();
}

export function ConfirmProvider({
	children,
	hasInitialConsent,
}: {
	children: ReactNode;
	hasInitialConsent: boolean;
}) {
	const t = useTranslations("confirmGate");
	const clientStoredConsent = useSyncExternalStore(subscribeConsent, getClientStoredConsent, () => false);
	const mounted = useSyncExternalStore(
		subscribeConsent,
		() => true,
		() => false,
	);
	const [sessionConfirmed, setSessionConfirmed] = useState(false);
	const [open, setOpen] = useState(false);
	const [age21, setAge21] = useState(false);
	const [ruo, setRuo] = useState(false);
	const pendingCb = useRef<(() => void) | null>(null);

	const hasConsent = hasInitialConsent || clientStoredConsent || sessionConfirmed;

	useEffect(() => {
		if (hasConsent) return;
		const timer = setTimeout(() => setOpen(true), AUTO_SHOW_DELAY);
		return () => clearTimeout(timer);
	}, [hasConsent]);

	useEffect(() => {
		if (!hasConsent || !pendingCb.current) return;
		const cb = pendingCb.current;
		pendingCb.current = null;
		cb();
	}, [hasConsent]);

	useEffect(() => {
		if (!open) return;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	const requestConfirm = useCallback(
		(cb?: () => void) => {
			if (hasConsent) {
				cb?.();
				return;
			}
			pendingCb.current = cb ?? null;
			setAge21(false);
			setRuo(false);
			setOpen(true);
		},
		[hasConsent],
	);

	const confirm = () => {
		if (!age21 || !ruo) return;
		setConsentPersistence();
		setSessionConfirmed(true);
		setOpen(false);
	};

	const ctxValue = useMemo<ConfirmContextValue>(
		() => ({ isConfirmed: hasConsent && mounted, requestConfirm }),
		[hasConsent, mounted, requestConfirm],
	);

	return (
		<ConfirmContext.Provider value={ctxValue}>
			{children}

			{open && (
				<div
					className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center sm:p-4"
					role="dialog"
					aria-modal="true"
					aria-labelledby="ruo-gate-title"
				>
					<div className="bg-primary-dark/85 absolute inset-0 backdrop-blur-md" aria-hidden />

					<div className="relative max-h-[80dvh] w-full max-w-full overflow-y-auto rounded-t-2xl bg-white shadow-[0_50px_120px_-30px_rgba(8,39,74,0.7)] sm:max-h-[92vh] sm:max-w-md sm:rounded-2xl">
						<div className="flex items-center justify-between bg-primary-dark px-3 py-2 font-mono text-[0.48rem] uppercase tracking-[0.14em] text-white sm:px-5 sm:py-2.5 sm:text-[0.58rem] sm:tracking-[0.2em]">
							<span className="flex items-center gap-1.5">
								<span className="relative flex h-1.5 w-1.5">
									<span className="absolute inset-0 animate-ping rounded-full bg-status-soon opacity-75" />
									<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-status-soon" />
								</span>
								{t("stripRequired")}
							</span>
							<span className="text-white/65">{t("stripLegal")}</span>
						</div>

						<div className="px-4 pb-4 pt-3 sm:p-6">
							<div className="font-mono text-[0.5rem] uppercase tracking-[0.16em] text-status-soon sm:text-[0.6rem] sm:tracking-[0.2em]">
								{t("eyebrow")}
							</div>
							<h2
								id="ruo-gate-title"
								className="text-text mt-1.5 font-display text-[1.05rem] font-medium leading-[1.2] tracking-[-0.015em] sm:mt-2.5 sm:text-[1.4rem] sm:leading-[1.18]"
							>
								{t("titleBefore")}
								<span className="text-primary">{t("titleHighlight")}</span>
							</h2>

							<div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2.5">
								<label className="bg-bg-2 flex cursor-pointer items-start gap-2 rounded-lg border border-border px-2.5 py-2 transition-colors hover:border-primary sm:gap-3 sm:rounded-xl sm:px-4 sm:py-3">
									<input
										type="checkbox"
										checked={age21}
										onChange={(e) => setAge21(e.target.checked)}
										className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
									/>
									<span className="text-text text-[0.72rem] leading-snug sm:text-[0.88rem]">
										{t("agreeAge")}
									</span>
								</label>

								<label className="bg-bg-2 flex cursor-pointer items-start gap-2 rounded-lg border border-border px-2.5 py-2 transition-colors hover:border-primary sm:gap-3 sm:rounded-xl sm:px-4 sm:py-3">
									<input
										type="checkbox"
										checked={ruo}
										onChange={(e) => setRuo(e.target.checked)}
										className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
									/>
									<span className="text-text text-[0.72rem] leading-snug sm:text-[0.88rem]">
										{t("agreeResearchBefore")}
										<strong>{t("agreeResearchBold")}</strong>
										{t("agreeResearchAfter")}
										<LinkWithChannel
											href="/research-use-only"
											className="text-primary underline-offset-2 hover:underline"
										>
											{t("ruoLink")}
										</LinkWithChannel>
										{t("agreeResearchEnd")}
									</span>
								</label>
							</div>

							<button
								type="button"
								onClick={confirm}
								disabled={!age21 || !ruo}
								className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-[0.62rem] font-medium uppercase tracking-[0.12em] text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50 sm:mt-5 sm:py-3 sm:text-[0.7rem] sm:tracking-[0.15em]"
							>
								{t("confirmButton")}
								{age21 && ruo && <span aria-hidden>→</span>}
							</button>

							<p className="text-text-3 mt-2 text-center font-mono text-[0.46rem] uppercase leading-relaxed tracking-[0.12em] sm:mt-3 sm:text-[0.55rem] sm:tracking-[0.16em]">
								{t("footerNote")}
							</p>
						</div>
					</div>
				</div>
			)}
		</ConfirmContext.Provider>
	);
}

export function useConfirm(): ConfirmContextValue {
	const ctx = useContext(ConfirmContext);
	if (!ctx) {
		return {
			isConfirmed: true,
			requestConfirm: (cb?: () => void) => cb?.(),
		};
	}
	return ctx;
}
